import { Box, ThemeProvider } from '@mui/material';
import { SLICE_NAME } from 'constants/common';
import { THEME_KEY, VMP_THEME } from 'constants/theme';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { themeService } from 'services';
import { setConfigs } from 'store/themeSlice';
import { normalizeColors } from 'utils/functions';
import defaultColors from './colors';
import generateTheme from './generateTheme';

const ThemeContext = createContext();

const DERIVED_CSS_VARIABLES = ['--primary90', '--text700', '--focus-s1'];

export const applyThemeCssVariables = (config) => {
    if (!config || typeof document === 'undefined') return;

    const rootStyle = document.documentElement.style;
    const primary = config[THEME_KEY.PRIMARY];
    const secondary = config[THEME_KEY.SECONDARY];
    const text = config[THEME_KEY.TEXT];
    const font = config[THEME_KEY.FONT];
    const isDefault = Boolean(config[THEME_KEY.IS_DEFAULT]);

    rootStyle.setProperty('--main', primary);
    rootStyle.setProperty('--focus', primary);
    rootStyle.setProperty('--secondary', secondary);
    rootStyle.setProperty('--text', text);
    rootStyle.setProperty('--font-family', font);

    if (isDefault) {
        DERIVED_CSS_VARIABLES.forEach((variable) => rootStyle.removeProperty(variable));
        return;
    }

    const normalizedColors = normalizeColors({ primary, secondary, text, isVMPTheme: false });
    rootStyle.setProperty('--primary90', normalizedColors.P3);
    rootStyle.setProperty('--text700', normalizedColors.T1);
    rootStyle.setProperty('--focus-s1', normalizedColors.S1);
};

export const useThemeController = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useThemeController phải dùng trong ThemeProviderCustom');
    return ctx;
};

const ThemeProviderCustom = ({ children }) => {
    const dispatch = useDispatch();
    const [colors, setColors] = useState(defaultColors);
    const theme = useMemo(() => generateTheme(colors), [colors]);
    const initialConfig = useSelector((state) => state[SLICE_NAME.THEME].initialConfig);
    const defaultConfig = useSelector((state) => state[SLICE_NAME.THEME].defaultConfig);
    const fetched = useRef(false);
    const [themeLoaded, setThemeLoaded] = useState(false);

    const updateTheme = (newColors) => {
        setColors((prev) => ({ ...prev, ...newColors }));
    };

    // Fetch theme system once at app level
    useEffect(() => {
        if (fetched.current) return;

        const controller = new AbortController();
        const signal = controller.signal;

        const fetchThemeSystem = async () => {
            fetched.current = true;

            const timeout = setTimeout(() => {
                controller.abort();
            }, 1500);

            try {
                const res = await themeService.getThemeSystem(signal);
                clearTimeout(timeout);

                if (res) {
                    batch(() => {
                        dispatch(
                            setConfigs({
                                defaultConfig: res,
                                initialConfig: res,
                            }),
                        );
                    });
                }
            } catch (error) {
                const fallback = defaultConfig || VMP_THEME;
                batch(() => {
                    dispatch(
                        setConfigs({
                            defaultConfig: fallback,
                            initialConfig: fallback,
                        }),
                    );
                });
            } finally {
                setThemeLoaded(true);
            }
        };

        fetchThemeSystem();

        return () => controller.abort();
    }, [defaultConfig, dispatch]);

    useEffect(() => {
        if (initialConfig) {
            setColors((prev) => ({ ...prev, ...initialConfig }));
            applyThemeCssVariables(initialConfig);
        }
    }, [initialConfig]);

    if (!themeLoaded) return <Box></Box>;

    return (
        <ThemeContext.Provider value={{ colors, updateTheme }}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ThemeContext.Provider>
    );
};

export default ThemeProviderCustom;
